import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Link as MuiLink,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useGetList, useNotify } from 'react-admin';
import { getToken } from '../authentication/authProvider';

const API_URL = (process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/').trim();

/** The agent kill switches, in gating order. Master gates the sweeper + lead jobs. */
const AGENT_FLAGS: { key: string; label: string; hint: string }[] = [
    {
        key: 'lead_agent_enabled',
        label: 'Master — sweeper & lead jobs',
        hint: 'Gates the 10-minute sweeper and lead job handling. Off pauses everything swept below.',
    },
    {
        key: 'agent_enabled_lead_followup',
        label: 'Lead follow-up',
        hint: 'Pre-conversion SMS follow-ups (goal: get the arrangement form filled).',
    },
    {
        key: 'agent_enabled_case_active',
        label: 'Case inbound replies',
        hint: 'Reads and answers inbound SMS on active cases (or escalates to staff).',
    },
    {
        key: 'agent_enabled_case_status_change',
        label: 'Case status updates (retired in v2)',
        hint: 'Drafted a family SMS when the cremation was scheduled; 4 of 5 were rejected. Off since v2 Phase 1 — the cremation-day notice is still to be decided.',
    },
    {
        key: 'agent_enabled_case_objective',
        label: 'Journey objectives',
        hint: 'Proactive case driver: transport dispatch & acknowledgment, SP3 chase, delivery details, missing info. Suggest-only; also gates the 1h transport-ack timer. Needs Master on to be swept.',
    },
    {
        key: 'agent_enabled_ingest',
        label: 'Paperwork ingestion',
        hint: "Parses a family's data reply (SIN, DOB, place of birth…) into proposed field writes on the case's feed for one-click review & save. Human-approved; never auto-writes.",
    },
    {
        key: 'agent_v1_reply_drafts',
        label: 'Reply drafts on the board (retired in v2)',
        hint: 'Drafted a reply to every family text; 3% were sent as written. Off since v2 Phase 1. Urgent inbound (complaints, a new death) still DMs the case owner either way.',
    },
    {
        key: 'agent_v1_missing_info_drafts',
        label: 'Missing-info SMS drafts (retired in v2)',
        hint: 'Drafted a "we still need…" SMS after delivery; 2 sent, 19 dismissed. Off since v2 Phase 1.',
    },
];

/** Delivery self-scheduling: master first, then the send switch. */
const DELIVERY_FLAGS: { key: string; label: string; hint: string }[] = [
    {
        key: 'delivery_scheduling_enabled',
        label: 'Master — delivery self-scheduling',
        hint: 'Gates the 7am ready-for-delivery sweep, the family scheduling invite, and the day-of messages. Off = nothing runs, not even a Slack preview.',
    },
    {
        key: 'delivery_invite_enabled',
        label: 'Send to families (off = test)',
        hint: 'Off is test mode: every message is composed and posted to the deliveries Slack channel instead of sent. On means families are actually texted and emailed. Needs Master on.',
    },
];

/**
 * v2 automatic family texts. Each is off | shadow | live | direct:
 *   shadow → a Slack card to the case owner (Send / Edit / Reject); nothing
 *            goes without a click
 *   live   → the same card; it goes by itself after the delay (default 30 min)
 *   direct → sent at once, no card (for texts proven by their sent-as-is rate)
 */
const RAILS_FLAGS: { key: string; label: string; hint: string; mode: true }[] = [
    {
        key: 'rails_milestone_contract_sent',
        label: 'Milestone — contract sent',
        hint: '"I\'ve emailed you the contract…" — a card to the owner as soon as it goes out, unless someone texted the family in the hour before. Weekdays 9–18.',
        mode: true,
    },
    {
        key: 'rails_milestone_contract_signed',
        label: 'Milestone — contract signed',
        hint: '"I\'ve received the signed contract…" Same rules; not sent if the case has moved past transport by send time.',
        mode: true,
    },
    {
        key: 'rails_milestone_transport_arranged',
        label: 'Milestone — transport arranged',
        hint: 'Says transport has been ARRANGED (we have no pickup-complete signal, so never "in our care"). Skipped if the case moved on or the dispatch is more than 18h old.',
        mode: true,
    },
];

type SendMode = 'off' | 'shadow' | 'live' | 'direct';

/** enabled=false → off; value 'live[:N]' → live; 'direct' → direct; otherwise shadow. */
const modeOf = (f?: FeatureFlag): SendMode =>
    !f || f.enabled === false
        ? 'off'
        : f.value === 'direct'
        ? 'direct'
        : /^live(:\d+)?$/.test(f.value || '')
        ? 'live'
        : 'shadow';

/** The live delay kept on the value ("live:45" / "shadow:45"); null = default 30. */
const delayOf = (f?: FeatureFlag): number | null => {
    const m = (f?.value || '').match(/^(?:live|shadow):(\d+)$/);
    return m ? Number(m[1]) : null;
};

const FLAG_GROUPS: {
    title: string;
    items: { key: string; label: string; hint: string; mode?: true }[];
}[] = [
    { title: 'Agent kill switches', items: AGENT_FLAGS },
    { title: 'Delivery self-scheduling', items: DELIVERY_FLAGS },
    { title: 'v2 family texts (off · shadow · live · direct)', items: RAILS_FLAGS },
].filter(group => group.items.length > 0);

interface ActionRecord {
    tool?: string;
    input?: Record<string, unknown>;
    result?: {
        success?: boolean;
        action?: string;
        detail?: string;
        blocked_reason?: string;
    };
    reasoning?: string;
}

interface AgentRun {
    id: string | number;
    case_id: string | number | null;
    lead_id: string | null;
    trigger_type: string | null;
    trigger_detail: string | null;
    state: string | null;
    stop_reason: string | null;
    error: string | null;
    duration_ms: number | null;
    actions_taken: unknown;
    tool_calls: unknown;
    created_at: string;
}

interface FeatureFlag {
    key: string;
    enabled: boolean;
    value?: string | null;
    description: string | null;
    updated_at: string;
    updated_by: string | null;
}

interface PhaseOneMetrics {
    rails: {
        byKind: Record<
            string,
            {
                cards: number;
                open: number;
                decided: number;
                sentAsIsPct: number | null;
                editedPct: number | null;
                rejectedPct: number | null;
                autoSentPct: number | null;
                skippedAtSendPct: number | null;
                direct: number;
            }
        >;
        duplicates: { kind: string; dedupe_key: string; created_at: string }[];
    };
    staffSms: { cases: number; staffTypedPerCase: number | null };
}

/** The v2 Phase 1 gate (last 7 days): no duplicated texts; card outcomes per text; staff-typed SMS per case trending to ≤ 6. */
const PhaseOneGate = ({ metrics }: { metrics: PhaseOneMetrics | null }) => {
    if (!metrics) return null;
    const collisions = metrics.rails.duplicates.length;
    const pct = (n: number | null) => (n == null ? '—' : `${n}%`);
    const kinds = Object.entries(metrics.rails.byKind);
    return (
        <Paper sx={{ p: 2, flex: 1, minWidth: 420 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                v2 Phase 1 gate · last 7 days
            </Typography>
            <Stack spacing={0.5}>
                <Box>
                    <Chip
                        size="small"
                        color={collisions ? 'error' : 'success'}
                        label={`${collisions} texts staff sent again after Cleo (gate: 0)`}
                    />
                </Box>
                {kinds.map(([kind, o]) => (
                    <Typography key={kind} variant="caption" color="text.secondary" display="block">
                        {kind.replace('milestone_', '')}: {o.cards} cards · sent as is {pct(o.sentAsIsPct)} ·
                        edited {pct(o.editedPct)} · rejected {pct(o.rejectedPct)} · auto-sent{' '}
                        {pct(o.autoSentPct)} · not sent at send time {pct(o.skippedAtSendPct)}
                        {o.direct ? ` · ${o.direct} direct` : ''}
                    </Typography>
                ))}
                <Typography variant="caption" color="text.secondary" display="block">
                    Staff-typed SMS per case so far ({metrics.staffSms.cases} cases signed this
                    week): {metrics.staffSms.staffTypedPerCase ?? '—'} · target ≤ 6
                </Typography>
            </Stack>
        </Paper>
    );
};

const formatDuration = (ms: number | null) =>
    ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

const countActions = (actions: unknown) =>
    Array.isArray(actions) ? actions.length : 0;

const apiUrl = (path: string) =>
    `${API_URL.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

/** Pull the first action's reasoning/tool/result for the inline detail view. */
const firstAction = (actions: unknown): ActionRecord | null => {
    if (!Array.isArray(actions) || actions.length === 0) return null;
    return actions[0] as ActionRecord;
};

const RunDetails: React.FC<{ run: AgentRun }> = ({ run }) => {
    const action = firstAction(run.actions_taken);
    const reasoning = action?.reasoning;
    const tool = action?.tool;
    const toolInput = action?.input;
    const result = action?.result;

    const adminLink = run.lead_id
        ? `/leads/${run.lead_id}/show`
        : run.case_id
        ? `/funerals/${run.case_id}/show`
        : null;

    return (
        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Stack spacing={2}>
                {reasoning && (
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Reasoning
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
                        >
                            {reasoning}
                        </Typography>
                    </Box>
                )}

                {tool && (
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Tool
                        </Typography>
                        <Typography variant="body2">
                            <code>{tool}</code>
                        </Typography>
                        {toolInput && Object.keys(toolInput).length > 0 && (
                            <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                                {Object.entries(toolInput).map(([k, v]) => (
                                    <Typography key={k} variant="body2" sx={{ mb: 0.5 }}>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mr: 1 }}
                                        >
                                            {k}:
                                        </Typography>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {typeof v === 'string' ? v : JSON.stringify(v)}
                                        </Typography>
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {result && (
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Result
                        </Typography>
                        <Typography variant="body2">
                            {result.success === false ? '✗ ' : '✓ '}
                            {result.detail ?? result.action ?? '(no detail)'}
                            {result.blocked_reason && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={result.blocked_reason}
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Typography>
                    </Box>
                )}

                {!action && run.error && (
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Outcome note
                        </Typography>
                        <Typography variant="body2">{run.error}</Typography>
                    </Box>
                )}

                <Divider />

                <Stack
                    direction="row"
                    spacing={3}
                    sx={{ flexWrap: 'wrap' }}
                    alignItems="center"
                >
                    {run.lead_id && (
                        <Typography variant="caption" color="text.secondary">
                            Lead: <code>{run.lead_id}</code>
                        </Typography>
                    )}
                    {run.case_id && (
                        <Typography variant="caption" color="text.secondary">
                            Case: <code>{run.case_id}</code>
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        Run ID: <code>{run.id}</code>
                    </Typography>
                    {adminLink && (
                        <MuiLink href={`#${adminLink}`} variant="caption">
                            Open in admin →
                        </MuiLink>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default function Phase0Page() {
    const notify = useNotify();
    const { data, isLoading, error } = useGetList<AgentRun>('agent_runs', {
        pagination: { page: 1, perPage: 50 },
        sort: { field: 'created_at', order: 'DESC' },
    });

    const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
    const toggleRow = (id: string | number) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const [flags, setFlags] = useState<Record<string, FeatureFlag>>({});
    const [flagLoading, setFlagLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [flagError, setFlagError] = useState<string | null>(null);

    const loadFlags = useCallback(async () => {
        try {
            const token = await getToken();
            const res = await fetch(apiUrl('/feature-flags'), {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const body = (await res.json()) as FeatureFlag[];
            setFlags(Object.fromEntries(body.map(f => [f.key, f])));
            setFlagError(null);
        } catch (e: any) {
            setFlagError(e.message || 'Failed to load agent kill switches');
        } finally {
            setFlagLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadFlags();
    }, [loadFlags]);

    const [gate, setGate] = useState<PhaseOneMetrics | null>(null);
    useEffect(() => {
        void (async () => {
            try {
                const token = await getToken();
                const res = await fetch(apiUrl('/agent/v2/metrics?days=7'), {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setGate((await res.json()) as PhaseOneMetrics);
            } catch {
                // The gate card is informational; the page works without it.
            }
        })();
    }, []);

    const handleToggle = async (key: string, label: string, enabled: boolean) => {
        setSavingKey(key);
        try {
            const token = await getToken();
            const res = await fetch(apiUrl(`/feature-flags/${key}`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ enabled }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const body = (await res.json()) as FeatureFlag;
            setFlags(prev => ({ ...prev, [key]: body }));
            notify(`${label} ${enabled ? 'enabled' : 'paused'}`, { type: 'success' });
        } catch (e: any) {
            notify(`Failed to update ${label}: ${e.message}`, { type: 'error' });
        } finally {
            setSavingKey(null);
        }
    };

    const handleMode = async (key: string, label: string, mode: SendMode) => {
        if (
            (mode === 'live' || mode === 'direct') &&
            !window.confirm(
                mode === 'live'
                    ? `${label}: go live? Each text goes to the family by itself unless someone acts on its card.`
                    : `${label}: send directly? Families are texted at the milestone with no card.`,
            )
        ) {
            return;
        }
        const delay = delayOf(flags[key]);
        setSavingKey(key);
        try {
            const token = await getToken();
            const res = await fetch(apiUrl(`/feature-flags/${key}`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(
                    mode === 'off'
                        ? { enabled: false }
                        : {
                              enabled: true,
                              // Keep a custom live delay across mode switches.
                              value: mode !== 'direct' && delay ? `${mode}:${delay}` : mode,
                          },
                ),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const body = (await res.json()) as FeatureFlag;
            setFlags(prev => ({ ...prev, [key]: body }));
            notify(`${label}: ${mode}`, { type: 'success' });
        } catch (e: any) {
            notify(`Failed to update ${label}: ${e.message}`, { type: 'error' });
        } finally {
            setSavingKey(null);
        }
    };

    const runs = useMemo(() => data ?? [], [data]);
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const runsToday = runs.filter(r => new Date(r.created_at) >= today).length;
    // The service overloads `error` to record benign "why no action was taken"
    // strings (e.g. "Lead status: cooling_off", "LLM decided no action needed").
    // A real failure is what stop_reason='error' captures.
    const errorsToday = runs.filter(
        r => new Date(r.created_at) >= today && r.stop_reason === 'error',
    ).length;

    const COL_COUNT = 8;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Phase 0 — Agent Observability
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Recent runs of the autonomous lead-follow-up agent. Click a row to see the
                agent's reasoning, tool call, and result for that run.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Paper sx={{ p: 2, minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary">
                        Runs today
                    </Typography>
                    <Typography variant="h5">{runsToday}</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary">
                        Errors today
                    </Typography>
                    <Typography variant="h5">{errorsToday}</Typography>
                </Paper>
                {FLAG_GROUPS.map(group => (
                <Paper key={group.title} sx={{ p: 2, flex: 1, minWidth: 420 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {group.title}
                    </Typography>
                    {flagLoading ? (
                        <CircularProgress size={20} />
                    ) : flagError ? (
                        <Alert severity="error" sx={{ py: 0 }}>
                            {flagError}
                        </Alert>
                    ) : (
                        <Stack spacing={0.5}>
                            {group.items.map(({ key, label, hint, mode }) => {
                                const f = flags[key];
                                return (
                                    <Box
                                        key={key}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                        }}
                                    >
                                        {mode ? (
                                            <ToggleButtonGroup
                                                size="small"
                                                exclusive
                                                value={modeOf(f)}
                                                disabled={!f || savingKey === key}
                                                onChange={(_, v: SendMode | null) => {
                                                    if (v && v !== modeOf(f))
                                                        void handleMode(key, label, v);
                                                }}
                                                sx={{ flexShrink: 0 }}
                                            >
                                                <ToggleButton value="off">Off</ToggleButton>
                                                <ToggleButton value="shadow">Shadow</ToggleButton>
                                                <ToggleButton value="live" color="warning">
                                                    Live
                                                </ToggleButton>
                                                <ToggleButton value="direct" color="error">
                                                    Direct
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        ) : (
                                            <Switch
                                                size="small"
                                                checked={!!f?.enabled}
                                                disabled={!f || savingKey === key}
                                                onChange={e =>
                                                    void handleToggle(key, label, e.target.checked)
                                                }
                                            />
                                        )}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" component="span">
                                                {label}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                            >
                                                {mode && f && modeOf(f) === 'live'
                                                    ? `Goes by itself ${delayOf(f) ?? 30} min after the card. `
                                                    : ''}
                                                {!f
                                                    ? 'Not deployed yet — appears once its migration is applied.'
                                                    : `${hint}${f.updated_by ? ` — last changed ${new Date(f.updated_at).toLocaleDateString()} by ${f.updated_by}` : ''}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>
                ))}
                <PhaseOneGate metrics={gate} />
            </Box>

            <Typography variant="h6" sx={{ mb: 1 }}>
                Recent agent runs
            </Typography>

            {isLoading && <CircularProgress />}
            {error && <Alert severity="error">Failed to load agent runs.</Alert>}
            {!isLoading && !error && (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 32 }} />
                                <TableCell>When</TableCell>
                                <TableCell>Policy</TableCell>
                                <TableCell>Trigger</TableCell>
                                <TableCell>Entity</TableCell>
                                <TableCell>Outcome</TableCell>
                                <TableCell align="right">Actions</TableCell>
                                <TableCell align="right">Duration</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {runs.map(run => {
                                const isOpen = expanded.has(run.id);
                                return (
                                    <React.Fragment key={run.id}>
                                        <TableRow
                                            hover
                                            onClick={() => toggleRow(run.id)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ width: 32 }}>
                                                <IconButton size="small">
                                                    {isOpen ? (
                                                        <KeyboardArrowDownIcon fontSize="small" />
                                                    ) : (
                                                        <KeyboardArrowRightIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(run.created_at).toLocaleString()}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={run.state ?? '—'}
                                                    color={
                                                        run.state === 'case_active'
                                                            ? 'primary'
                                                            : 'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{run.trigger_type ?? '—'}</TableCell>
                                            <TableCell>
                                                {run.case_id ?? run.trigger_detail ?? '—'}
                                            </TableCell>
                                            <TableCell>
                                                {run.stop_reason === 'error' ? (
                                                    <Chip
                                                        size="small"
                                                        color="error"
                                                        label={run.error ?? 'error'}
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        title={run.error ?? undefined}
                                                        label={run.stop_reason ?? 'ok'}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {countActions(run.actions_taken)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatDuration(run.duration_ms)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                colSpan={COL_COUNT}
                                                sx={{ p: 0, border: 0 }}
                                            >
                                                <Collapse in={isOpen} unmountOnExit>
                                                    <RunDetails run={run} />
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {runs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={COL_COUNT} align="center">
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ py: 2 }}
                                        >
                                            No agent runs yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
